import { createContext, ReactNode, useContext } from "react";

export interface PricingPlan {
  id: string;
  name: string;
  badge?: string;
  description?: string;
  priceDisplay?: string;
  currency?: string;
  hasThemeStudio?: boolean;
  features?: string[];
  button?: string;
  setupFee?: {
    label?: string;
    amount?: string;
    period?: string;
  };
  subscription?: {
    label?: string;
    amount?: string;
    note?: string;
  };
}

export interface PricingContent {
  title: string;
  subtitle: string;
  plans: PricingPlan[];
}

const PricingContext = createContext<PricingContent | null>(null);

export const defaultPricingContent: PricingContent = {
  title: "Pricing",
  subtitle: "You pick the approach based on your confidence. We help you grow.",
  plans: [
    {
      id: "starter",
      name: "Starter",
      description: "Perfect for launching your first casino platform.",
      priceDisplay: "USD 99.00",
      currency: "USD",
      features: [
        "Up to 1,000 monthly active users",
        "Basic analytics",
        "Email support",
        "Standard themes",
      ],
      hasThemeStudio: false,
      button: "Get Started",
      setupFee: {
        label: "One-time setup fee:",
        amount: "$4'999",
        period: "",
      },
      subscription: {
        label: "Monthly subscription:",
        amount: "$799",
        note: "Revenue-sharing: 0% of the GRR",
      },
    },
    {
      id: "professional",
      name: "Professional",
      badge: "Popular",
      description: "Scale your operations with advanced features.",
      priceDisplay: "USD 299.00",
      currency: "USD",
      features: [
        "Up to 10,000 monthly active users",
        "Advanced analytics dashboard",
        "Priority support",
        "Custom themes",
        "A/B testing",
      ],
      hasThemeStudio: true,
      button: "Go Pro",
      setupFee: {
        label: "One-time setup fee:",
        amount: "$9'999",
        period: "",
      },
      subscription: {
        label: "Monthly subscription:",
        amount: "$1,499",
        note: "Revenue-sharing: 5% of the GGR",
      },
    },
    {
      id: "enterprise",
      name: "Enterprise",
      description: "Advanced customization and dedicated support.",
      priceDisplay: "USD 999.00",
      currency: "USD",
      features: [
        "Unlimited monthly active users",
        "Dedicated account manager",
        "Custom integrations",
        "SLA-backed uptime",
        "On-premise options",
      ],
      hasThemeStudio: true,
      button: "Contact Sales",
      setupFee: {
        label: "One-time setup fee:",
        amount: "$19'999",
        period: "",
      },
      subscription: {
        label: "Monthly subscription:",
        amount: "None",
        note: "Revenue-sharing: 10% of the GGR",
      },
    },
  ],
};

interface PricingProviderProps {
  value: PricingContent | null;
  children: ReactNode;
}

export const PricingProvider = ({ value, children }: PricingProviderProps) => {
  return <PricingContext.Provider value={value}>{children}</PricingContext.Provider>;
};

export const usePricing = () => {
  return useContext(PricingContext);
};


